import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAccountDeletionStatus,
  useRequestAccountDeletion,
  useCancelAccountDeletion,
} from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { ConfirmDestructiveAction } from "../components/ConfirmDestructiveAction";

/**
 * /profile/account-delete — two-state surface.
 *
 *   not-requested: explanatory copy + password-gated "Delete account"
 *                  button (D30: PII anonymized after grace, but
 *                  transactions and adjustments remain so merchants'
 *                  reports stay coherent).
 *   in-grace:      banner + "Cancel deletion" CTA. Status query is
 *                  shared with the cross-cutting banner via
 *                  ["account-deletion-status"].
 */
export function ProfileAccountDeletePage() {
  const { isAuthenticated, signOut } = useAuth();
  const qc = useQueryClient();
  const statusQ = useAccountDeletionStatus({ enabled: isAuthenticated });
  const requestMut = useRequestAccountDeletion();
  const cancelMut = useCancelAccountDeletion();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) return <Redirect to="/" />;

  const inGrace = Boolean(statusQ.data?.deletionRequestedAt);
  const scheduled = statusQ.data?.scheduledCompletionAt
    ? new Date(statusQ.data.scheduledCompletionAt)
    : null;

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Delete my account</h1>

        {statusQ.isLoading ? (
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        ) : inGrace ? (
          <section className="mt-6 card border-red-200 bg-red-50">
            <h2 className="text-base font-semibold text-red-900">
              Deletion scheduled
            </h2>
            <p className="mt-2 text-sm text-red-900">
              We'll permanently delete your profile on{" "}
              {scheduled?.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
              . You can change your mind any time before then.
            </p>
            <button
              type="button"
              className="btn-primary w-full mt-4"
              disabled={cancelMut.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await cancelMut.mutateAsync();
                  await qc.invalidateQueries({
                    queryKey: ["account-deletion-status"],
                  });
                } catch (err) {
                  const e = err as { apiError?: { message?: string } };
                  setError(e.apiError?.message ?? "Couldn't cancel.");
                }
              }}
            >
              {cancelMut.isPending ? "Cancelling…" : "Cancel deletion"}
            </button>
            {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}
          </section>
        ) : (
          <>
            <section className="mt-6 card">
              <p className="text-sm text-ink-dark">
                Deleting your account will:
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-ink-dark list-disc pl-5">
                <li>Hide your profile from all connected merchants.</li>
                <li>
                  Anonymize your name, email, and phone after a 7-day grace
                  period.
                </li>
                <li>
                  Preserve transaction history under an anonymous ID so your
                  merchants' reports stay accurate.
                </li>
                <li>
                  Be reversible during the 7-day grace period — just sign back
                  in and tap "Cancel deletion".
                </li>
              </ul>
            </section>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="mt-6 w-full rounded-full bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700"
            >
              Delete my account
            </button>
          </>
        )}
      </div>

      {confirmOpen && (
        <ConfirmDestructiveAction
          open
          title="Delete your Orbit account?"
          body={
            <div className="space-y-3">
              <p>Enter your password to confirm.</p>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
              />
            </div>
          }
          reasonPlaceholder="Tell us why (optional)"
          confirmLabel="Schedule deletion"
          busy={requestMut.isPending}
          onCancel={() => {
            setConfirmOpen(false);
            setPassword("");
            setReason("");
          }}
          onConfirm={async (r) => {
            if (password.length < 1) {
              throw {
                apiError: { message: "Please enter your password." },
              };
            }
            await requestMut.mutateAsync({
              password,
              reason: (r ?? reason.trim()) || undefined,
            });
            await qc.invalidateQueries({
              queryKey: ["account-deletion-status"],
            });
            setConfirmOpen(false);
            setPassword("");
            setReason("");
            // Stay signed in during grace so the customer can cancel
            // without re-logging-in. They'll see the in-grace state on
            // the next render.
            void signOut;
          }}
        />
      )}
    </div>
  );
}
